import React from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DoneAllOutlined,
  DoneOutlined,
  ErrorOutline,
  ErrorOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { formatText } from "../../helpers";
import { IAcitvityWhatsAppGroupConversationLogsProps } from "./Activity.types";
import { LoadState } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import {
  clearWhatsAppGroupChat,
  createGroup,
  fetchWhatsAppGroupConversationListAsync,
} from "../../redux/activity/activityActions";
import { IWhatsAppGroupActivity } from "../../redux/activity/activity.types";
import { ControlledCustomSelect, CustomFormLabel } from "../formsComponents";
import { useFormik } from "formik";
import { useTimezoneFormatter } from "../../hooks/useTimezoneFormatter";

export const ActivityWhatsAppGroupLogs: React.FC<
  IAcitvityWhatsAppGroupConversationLogsProps
> = (props) => {
  const { moduleInformation, customerName, phone } = props;
  const { list, loading, error, isGroupConnected, groups, hasMoreRecords } =
    useSelector((storeState: IStoreState) => storeState.acitivty.whatsAppGroup);
  const chatContainerRef = React.useRef<HTMLDivElement | null>(null);
  const hasNoRecords = loading === LoadState.Loaded && !isGroupConnected;

  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const { ftDateTime } = useTimezoneFormatter();

  const [tablePagination, setTablePagination] = React.useState({
    pageNumber: 1,
    rowsInPerPage: 100,
  });
  const [shouldScrollToBottom, setShouldScrollToBottom] = React.useState(true);
  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues: {
      group_id: "",
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.group_id) {
        errors.group_id = "Group is requried!";
      }
      return errors;
    },
    onSubmit: (values) => {
      dispatch(
        createGroup(
          {
            group_id: values.group_id,
            module_name: moduleInformation.moduleName,
            module_uuid: moduleInformation.moduleId,
            status: "ACTIVE",
          },
          (isSuccess) => {
            if (isSuccess) {
              fetchLogs();
            }
          },
        ),
      );
    },
  });

  const fetchLogs = async () => {
    dispatch(
      fetchWhatsAppGroupConversationListAsync(
        moduleInformation.moduleName,
        moduleInformation.moduleId,
        tablePagination.pageNumber,
        tablePagination.rowsInPerPage,
        true,
      ),
    );
  };

  const handleScroll = () => {
    if (loading === LoadState.InProgress) {
      return;
    }
    if (!chatContainerRef.current || !hasMoreRecords) return;
    const { scrollTop } = chatContainerRef.current;
    setShouldScrollToBottom(false);
    if (scrollTop <= 50) {
      dispatch(
        fetchWhatsAppGroupConversationListAsync(
          moduleInformation.moduleName,
          moduleInformation.moduleId,
          tablePagination.pageNumber + 1,
          tablePagination.rowsInPerPage + 100,
          false,
          () => {
            if (chatContainerRef.current) {
              const scrollHeightBefore = chatContainerRef.current.scrollHeight;
              setTablePagination({
                pageNumber: tablePagination.pageNumber + 1,
                rowsInPerPage: tablePagination.rowsInPerPage + 100,
              });
              setTimeout(() => {
                if (chatContainerRef.current) {
                  const scrollHeightAfter =
                    chatContainerRef.current?.scrollHeight;
                  chatContainerRef.current.scrollTop =
                    scrollHeightAfter - scrollHeightBefore;
                }
              }, 0);
            }
          },
        ),
      );
    }
  };

  const handleRefresh = () => {
    setTablePagination({ pageNumber: 1, rowsInPerPage: 100 });
    dispatch(
      fetchWhatsAppGroupConversationListAsync(
        moduleInformation.moduleName,
        moduleInformation.moduleId,
        1,
        50,
        true,
      ),
    );
    setShouldScrollToBottom(true);
  };

  React.useEffect(() => {
    const chatContainer = chatContainerRef.current;

    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loading]);

  React.useEffect(() => {
    if (shouldScrollToBottom && chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [list, shouldScrollToBottom]);

  React.useEffect(() => {
    fetchLogs();

    return () => {
      dispatch(clearWhatsAppGroupChat());
    };
  }, []);

  return (
    <>
      <Stack mt={3}>
        <Stack
          padding={1}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{
            backgroundColor: theme.palette.grey[200],
            borderRadius: 1,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            WhatsApp Conversation
          </Typography>
        </Stack>
        {!hasNoRecords && (
          <Stack
            ref={chatContainerRef}
            direction={"column"}
            spacing={1}
            mt={3}
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 1,
              maxHeight: "50vh",
              overflow: "auto",
            }}
          >
            {loading === LoadState.InProgress && (
              <>
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
              </>
            )}
            {loading === LoadState.Failed && error && (
              <>
                <Box
                  minHeight={100}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Box display={"flex"} justifyContent={"center"} mb={1}>
                    <ErrorOutline />
                  </Box>
                  <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                    <Typography
                      variant="body1"
                      textAlign={"center"}
                      fontWeight={600}
                    >
                      {error}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {list.map((item, index) => {
              return <Row key={index} data={item} currentPhone={phone} />;
            })}
          </Stack>
        )}

        {hasNoRecords && (
          <form onSubmit={handleSubmit}>
            <Stack direction={"column"} mb={1} spacing={1}>
              <CustomFormLabel>Create Group</CustomFormLabel>
              <ControlledCustomSelect
                value={values.group_id}
                name="group_id"
                fullWidth
                options={groups.map((x) => ({
                  label: x.name,
                  value: x.id,
                }))}
                displayEmpty
                placeholder="Select one"
                onChange={handleChange}
                error={errors.group_id ? true : false}
                helperText={errors.group_id}
              />
              <Button type="submit" variant="contained">
                Create
              </Button>
            </Stack>
          </form>
        )}

        <Grid item xs={12} md={11}>
          <Button
            variant="contained"
            fullWidth
            size="small"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Grid>
      </Stack>
    </>
  );
};

const Row: React.FC<{
  data: IWhatsAppGroupActivity;
  currentPhone: string;
}> = ({ data, currentPhone }) => {
  const theme = useTheme();
  const { ftDateTime } = useTimezoneFormatter();

  const [openView, setOpenView] = React.useState(false);

  const handleToggle = () => {
    setOpenView(!openView);
  };

  const renderStaus = React.useMemo(() => {
    if (data.status === "SENT") {
      return (
        <Tooltip title="Sent" placement="top" arrow>
          <DoneOutlined fontSize={"small"} color="disabled" />
        </Tooltip>
      );
    } else if (data.status === "DELIVERED") {
      return (
        <Tooltip title="Delivered" placement="top" arrow>
          <DoneAllOutlined fontSize={"small"} color="disabled" />
        </Tooltip>
      );
    } else if (data.status === "READ") {
      return (
        <Tooltip title="Seen" placement="top" arrow>
          <DoneAllOutlined color="secondary" fontSize={"small"} />
        </Tooltip>
      );
    } else if (["INITIATE"].includes(data.status)) {
      return <></>;
    } else {
      return (
        <Tooltip title={formatText(data.status)} placement="top" arrow>
          <ErrorOutlined color="error" fontSize="small" />
        </Tooltip>
      );
    }
  }, [data.status]);

  const isSender = false;

  return (
    <>
      <Stack position={"relative"} onClick={handleToggle}>
        <Stack>
          <Box
            display={"flex"}
            sx={{
              justifyContent: isSender ? "end" : "start",
            }}
          >
            <Box maxWidth={"85%"}>
              {data.content.map((messageData, index) => {
                return (
                  <Box key={index}>
                    {messageData.type === "BODY" || messageData.text ? (
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,

                          borderRadius: 2,
                          backgroundColor: isSender
                            ? theme.palette.primary.main
                            : "#fff",
                          color: isSender ? "#fff" : "unset",
                        }}
                      >
                        <Typography variant="body1" fontSize={"0.95rem"}>
                          {" "}
                          {messageData.text}
                        </Typography>
                      </Paper>
                    ) : (
                      <Stack direction={"column"} mt={1} spacing={1}>
                        {messageData.buttons.map((buttonData, index) => {
                          return (
                            <Button
                              sx={{ width: "70%" }}
                              variant="outlined"
                              size="small"
                            >
                              {buttonData.text}
                            </Button>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                );
              })}
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    ml: 1,
                    mt: 0.5,
                    textAlign: "end",
                    width: "100%",
                  }}
                >
                  {`(By - ${
                    data.from_name ? data.from_name : data.from_number || ""
                  }) ${ftDateTime(data.insert_ts)}`}
                </Typography>
                {isSender && <Stack>{renderStaus}</Stack>}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};
